import { detectBodyLandmarks, type BodyLandmarks } from "../../pose-detect";

/**
 * Pick the image from a product gallery that's most likely a "model
 * wearing the garment" shot — that's what Gemini's try-on prompt should
 * receive as image 2, NOT the flat-lay or close-up detail. The SDK's UI
 * still shows the first image in the gallery; this only affects what
 * Gemini sees.
 *
 * Scoring (uses MediaPipe BlazePose, already loaded for sizing):
 *  + 10 per visible body landmark (shoulders, hips, knees, etc.) → presence of a body
 *  + 50 if nose is visible              → front-facing model shot (best garment view)
 *  +  0 for flat-lay or close-up detail → no skeleton at all
 *
 * Result is cached per gallery-fingerprint for the lifetime of the tab so
 * repeat try-ons / regens don't re-run pose detection on the same images.
 */

const cache = new Map<string, string>();

// Pull a normalized point if it has the {x, y} shape we expect.
function pt(lm: BodyLandmarks, key: string): { x: number; y: number } | null {
  const v = (lm as any)[key];
  if (v && typeof v === "object" && typeof v.x === "number" && typeof v.y === "number") return v;
  return null;
}

function scoreLandmarks(lm: BodyLandmarks | null): number {
  if (!lm) return 0;

  // The thing that matters for a garment-reference shot is: do we see the
  // model's WHOLE BODY? A close-up portrait can have a high joint count
  // (face features score, shoulders score, etc.) but tells Gemini nothing
  // about how the garment hangs / drapes / sits on a person. So we score
  // FULL-BODY-NESS dominantly and treat nose / front-facing as a tiebreaker.
  const ankles      = pt(lm, "leftAnkle")    || pt(lm, "rightAnkle");
  const knees       = pt(lm, "leftKnee")     || pt(lm, "rightKnee");
  const hips        = pt(lm, "leftHip")      || pt(lm, "rightHip");
  const shoulders   = pt(lm, "leftShoulder") || pt(lm, "rightShoulder");
  const nose        = pt(lm, "nose");

  let score = 0;

  // Body-region bands. Each higher band only counts if the lower one was
  // hit — guarantees the order: ankles > knees > hips > shoulders.
  if (shoulders) score += 30;             // upper torso visible
  if (hips)      score += 60;             // mid-body visible
  if (knees)     score += 80;             // mid-thigh / knee visible
  if (ankles)    score += 200;            // ★ full body visible — what we want

  // Spatial-spread bonus: in a full-body model shot the landmarks span
  // most of the image vertically (head near top, feet near bottom). In a
  // portrait the landmarks cluster in the upper third. Reward big spread.
  const ys = [nose, shoulders, hips, knees, ankles]
    .filter((p): p is { x: number; y: number } => !!p)
    .map((p) => p.y);
  if (ys.length >= 2) {
    const spread = Math.max(...ys) - Math.min(...ys);   // 0..1
    score += Math.round(spread * 80);                   // up to +80
  }

  // Front-facing tie-breaker only — small. Used when two images both look
  // full-body but one shows the model facing forward and the other from
  // the back.
  if (nose) score += 10;

  return score;
}

async function scoreImage(url: string): Promise<number> {
  try {
    const lm = await detectBodyLandmarks(url);
    return scoreLandmarks(lm);
  } catch {
    return 0;
  }
}

export async function pickBestGarmentImage(images: string[] | null | undefined): Promise<string | null> {
  if (!images || !images.length) return null;
  if (images.length === 1) return images[0]!;

  const cacheKey = images.join("|");
  const cached = cache.get(cacheKey);
  if (cached) return cached;

  const t0 = Date.now();
  const scored = await Promise.all(images.map(async (url) => ({
    url,
    score: await scoreImage(url),
  })));
  scored.sort((a, b) => b.score - a.score);

  // If every image scored 0 (none had a body), keep the first image as a
  // safe default rather than picking arbitrarily.
  const best = (scored[0]?.score ?? 0) > 0 ? scored[0]!.url : images[0]!;
  cache.set(cacheKey, best);

  console.log(`[ps-sdk:garment-pick] ${Date.now() - t0}ms — chose ${images.indexOf(best)}/${images.length}`);
  for (const s of scored) console.log(`[ps-sdk:garment-pick]   ${s.score.toString().padStart(4, " ")}  ${s.url}`);

  return best;
}
